import { memo, useMemo } from "react";

import { ScrollArea } from "@/components/ui/scroll-area";

import { ShareAccessRow } from "./ShareAccessRow";
import { ShareBulkActionBar } from "./ShareBulkActionBar";
import { ShareHostRow } from "./ShareHostRow";
import type { IShareAccessListProps } from "./types";

const ShareAccessListComponent = ({
  hostName,
  hostHandle,
  hostInitials,
  entries,
  canManage,
  isLoading,
  isRemoving,
  onRemove,
  selectableIds,
  selectedIds,
  isBulkPending,
  onSelectOne,
  onSelectAll,
  onDeselectAll,
  onBulkRemove,
  labels,
}: IShareAccessListProps): JSX.Element => {
  const selectableSet = useMemo(() => new Set(selectableIds), [selectableIds]);

  const isAllSelected = useMemo(
    () =>
      selectableIds.length > 0 &&
      selectableIds.every((id) => selectedIds.has(id)),
    [selectableIds, selectedIds],
  );

  const shouldReserveCheckboxSpace = selectableIds.length > 0;

  const shouldReserveMenuSpace = useMemo(
    () =>
      canManage ||
      entries.some(
        (entry) => entry.isCurrentUser || entry.isManageableByViewer === true,
      ),
    [canManage, entries],
  );

  return (
    <div aria-busy={isBulkPending}>
      <h3 className="mb-3 text-sm font-semibold">{labels.title}</h3>

      <ShareBulkActionBar
        selectedCount={selectedIds.size}
        totalSelectableCount={selectableIds.length}
        isAllSelected={isAllSelected}
        isBulkPending={isBulkPending}
        onSelectAll={onSelectAll}
        onDeselectAll={onDeselectAll}
        onBulkRemove={onBulkRemove}
        labels={labels.bulk}
      />

      <ScrollArea viewportClassName="max-h-[min(280px,40vh)]">
        {isLoading ? (
          <div className="py-4 text-sm text-muted-foreground">
            {labels.loading}
          </div>
        ) : (
          <div className="space-y-4">
            <ShareHostRow
              hostName={hostName}
              hostHandle={hostHandle}
              hostInitials={hostInitials}
              shouldReserveCheckboxSpace={shouldReserveCheckboxSpace}
              shouldReserveMenuSpace={shouldReserveMenuSpace}
              hostBadgeLabel={labels.hostBadge}
            />

            {entries.map((entry) => {
              const shouldShowMenu =
                canManage ||
                entry.isCurrentUser ||
                entry.isManageableByViewer === true;
              const isSelectable = selectableSet.has(entry.id);

              return (
                <ShareAccessRow
                  key={entry.id}
                  entry={entry}
                  shouldShowMenu={shouldShowMenu}
                  isRemoving={isRemoving}
                  onRemove={onRemove}
                  isSelectable={isSelectable}
                  isSelected={selectedIds.has(entry.id)}
                  isBulkPending={isBulkPending}
                  onSelectChange={onSelectOne}
                  shouldReserveCheckboxSpace={shouldReserveCheckboxSpace}
                  shouldReserveMenuSpace={shouldReserveMenuSpace}
                  labels={labels.row}
                />
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export const ShareAccessList = memo(ShareAccessListComponent);

ShareAccessList.displayName = "ShareAccessList";
